import React, {useEffect, useState} from 'react';
import moment from "moment";
import { Link, useLocation, useMatch, useOutletContext } from "react-router-dom";
import { fetchAPI } from "@/api";
import PaymentMethod from "@/components/PaymentMethod";
import DetailsTable from "@/components/DetailsTable";
import RecurringPaymentsTable from "@/components/Tables/RecurringPaymentsTable";
// @ts-ignore
import { formatPrice } from "invitemember-utils-public/currencies";
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import ReloadCircle from '@/images/icons/ReloadCircle';
import BackButtonPage from '@/components/BackButtonPage';

function RecurringPayment() {
  const location = useLocation();
  const [payment, setPayment] = useState(location.state);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {auth, project} = useOutletContext();
  let match = useMatch("/sales/recurringPayment/:recurringPaymentId");

  useEffect(() => {
    const fetchData = async() => {
      try {
        setLoading(true);
        const response = await fetchAPI("/project/recurringPayment", auth.user.token, {
          projectId: project.projectId,
          recurringPaymentId: match.params.recurringPaymentId
        });
        if (!response.ok) {
          throw new Error(`This is an HTTP error: The status is ${response.status}`);
        }
        let actualData = await response.json();
        setPayment(actualData);
        setError(null);
      } catch (error) {
        let errorMessage = 'An unexpected error occurred';
        if (error.response?.message) {
          errorMessage = error.response.message;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [match]);

  // let imCommission = <div>&mdash;</div>;
  let imCommission = <div>$0</div>;
  if (payment && payment.imCommission) {
    imCommission = <div>{payment.imCommission.formattedAmount} {payment.imCommission.currency} converted to ${payment.imCommission.amountUSD}</div>
  }

  let details = [];
  details.push({ label: "Customer", value: !payment ? null : <Link to={"/user/" + payment?.userId} className="underline hover:no-underline">{payment?.fullName}</Link>, none: "—" });
  details.push({ label: "Received", value:  !payment ? null : moment(payment?.receivedAt).format("MMM D, LT"), none: "Unknown" });

  details.push({ label: "Plan", value: payment?.planName, none: "—" });
  details.push({ label: "Subscription", value: !payment ? null : <Link to={"/sales/subscription/" + payment?.subscriptionId} className="underline hover:no-underline">{payment?.subscriptionId}</Link>, none: "—" });

  let methodWithDetails = null;
  if (payment?.stripeChargeId) {
    methodWithDetails = <div>Stripe / <a className="underline hover:no-underline" target="_blank" rel="noopener noreferrer" href={"https://dashboard.stripe.com/" + (payment?.liveMode ? "" : "test/") + "payments/" + payment?.stripeChargeId}>{payment?.stripeChargeId}</a></div>;
  } else if (payment?.coinPaymentsTxn) {
    methodWithDetails = <div>CoinPayments / {payment?.coinPaymentsTxn}</div>;
  } else if (payment?.paypalCaptureId) {
    methodWithDetails = <div>PayPal / {payment?.paypalCaptureId}</div>;
  } else if (payment?.paypalOrderId) {
    methodWithDetails = <div>PayPal / {payment?.paypalOrderId}</div>;
  } else if (payment?.accessCodeId) {
    methodWithDetails = <div>Access Code / {payment?.accessCodeId}</div>;
  } else if (payment?.razorpayPaymentId) {
    methodWithDetails = <div>Razorpay / {payment?.razorpayPaymentId}</div>;
  } else {
    methodWithDetails = <PaymentMethod method={payment?.method} showIcon={false} showMode={false} />;
  }

  details.push({ label: "Method", value: !payment ? null : methodWithDetails, none: "Unknown" });

  let feeInfo;
  if (payment?.imCommission?.amountUSD) {
    feeInfo = `$${payment.imCommission?.amountUSD.toFixed(2)} USD (${payment.imCommission?.percent}%)`;
  } else if (payment?.stripeApplicationFeeAmount) {
    feeInfo = `${(payment.stripeApplicationFeeAmount / 100).toFixed(2)} (${Math.round(payment.stripeApplicationFeeAmount / payment.amountUSD).toFixed(0)}%) via Stripe Connect`;
  }
  if (feeInfo) details.push({ label: "InviteMember Fee", value: !feeInfo ? null : feeInfo, none: "$0" });

  const ignoreLiveMode = payment && (payment.method.includes('coinpayments') || payment.method.includes('access code'));
  const testLabel = payment && (ignoreLiveMode ? false : !payment.liveMode);

  return (
    <StaticLayoutInner>
      <div className="relative">
        <BackButtonPage to={`/sales/recurringPayments`} title="Recurring payments" />
        <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start">
          <div className="flex items-center gap-2 mb-5">
            <ReloadCircle className="w-8 h-8" backgroundColor="#F7F6F2" />
            <h2 className="text-[#A5A5A5] text-sm/[1.3] font-medium">Recurring payment</h2>
          </div>
          <h1 className="h2 mb-5">
            {!payment && <div className={"animate-pulse bg-[#F2F2F2] rounded-full w-32 h-6 mt-1 inline-block"}></div>}
	          {payment && <span>{formatPrice(payment.amount, payment.currency)}</span>}{" "}
	          {payment && (payment.currency === "XTR" ?
		          <span className=""></span> :
		          <span className="uppercase"> {payment.currency}</span>)}
          </h1>
          <div className="flex gap-2">
            {payment && testLabel && <div className="badge -gray">Test Mode</div>}
          </div>
          <div className="mt-8 mb-1">
            <DetailsTable details={details} isLoading={isLoading} />
          </div>
        </div>
      </div>
      <div className="mt-8">
        <h2 className="h3 mb-4">Relevant recurring payments</h2>
        <RecurringPaymentsTable
          pageSize={10}
          delayLoading={!payment?.userId || !payment?.recurringPaymentId}
          filters={JSON.stringify({
            filters: [
              { key: "userId", condition: { eq: payment?.userId } },
              { key: "subscriptionId", condition: { eq: payment?.subscriptionId } },
              { key: "recurringPaymentId", condition: { ne: payment?.recurringPaymentId } }
            ]
          })}
        />
      </div>
    </StaticLayoutInner>
  );
}

export default RecurringPayment;
